import axios from 'axios';

const PDF_MIME = 'application/pdf';
const CSV_MIME = 'text/csv;charset=utf-8';

async function messageFromResponseData(data) {
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed.message || parsed.error || text;
      } catch {
        return text.slice(0, 200) || 'Request failed';
      }
    } catch {
      return 'Request failed';
    }
  }
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed.message || data;
    } catch {
      return data.slice(0, 200);
    }
  }
  return data?.message || 'Request failed';
}

function assertHtmlContent(html) {
  const trimmed = String(html || '').trim();
  if (!trimmed || trimmed.startsWith('{') || trimmed.startsWith('[')) {
    throw new Error('Server did not return a valid HTML preview');
  }
  return html;
}

/**
 * Reconstruct a PDF Blob when a proxy/axios turns binary into a JSON byte map
 * e.g. {"0":37,"1":80,"2":68,"3":70,...} (%PDF...)
 */
function coercePdfBlob(data, contentType = PDF_MIME) {
  if (data instanceof Blob) {
    return data;
  }
  if (data instanceof ArrayBuffer) {
    return new Blob([data], { type: contentType });
  }
  if (ArrayBuffer.isView(data)) {
    return new Blob([data], { type: contentType });
  }
  if (data?.type === 'Buffer' && Array.isArray(data.data)) {
    return new Blob([new Uint8Array(data.data)], { type: contentType });
  }
  if (Array.isArray(data)) {
    return new Blob([new Uint8Array(data)], { type: contentType });
  }
  if (data && typeof data === 'object' && data['0'] !== undefined && typeof data['0'] === 'number') {
    const keys = Object.keys(data).filter((k) => /^\d+$/.test(k));
    keys.sort((a, b) => Number(a) - Number(b));
    const bytes = new Uint8Array(keys.map((k) => data[k]));
    return new Blob([bytes], { type: contentType });
  }
  throw new Error('Unexpected PDF response format');
}

async function assertPdfBlob(blob) {
  const headerText = await new Response(blob.slice(0, 5)).text();
  if (!headerText.startsWith('%PDF')) {
    throw new Error(await messageFromResponseData(blob));
  }
  return blob;
}

async function fetchCsvText(url, method = 'get', body) {
  const config = {
    responseType: 'text',
    skipGlobalErrorToast: true,
    headers: { Accept: 'text/csv' }
  };
  const response = method === 'post'
    ? await axios.post(url, body, config)
    : await axios.get(url, config);

  const contentType = (response.headers['content-type'] || '').toLowerCase();
  if (contentType.includes('application/json')) {
    try {
      const parsed = JSON.parse(response.data);
      throw new Error(parsed.message || 'Server returned JSON instead of CSV');
    } catch (err) {
      if (err.message && !err.message.includes('JSON')) throw err;
      throw new Error(response.data?.slice?.(0, 200) || 'Server returned JSON instead of CSV');
    }
  }

  const text = String(response.data || '');
  if (!text.trim()) {
    throw new Error('Server returned an empty CSV file');
  }
  return text;
}

function csvFilenameFromPdfName(filename, fallback = 'report.csv') {
  const base = String(filename || fallback).replace(/\.pdf$/i, '');
  return `${base}.csv`;
}

export function triggerTextDownload(text, filename, mimeType = CSV_MIME) {
  const blob = new Blob([text], { type: mimeType });
  triggerBlobDownload(blob, filename);
}

async function fetchPdfArrayBuffer(url, method = 'get', body) {
  const config = {
    responseType: 'arraybuffer',
    skipGlobalErrorToast: true,
    headers: { Accept: PDF_MIME }
  };
  const response = method === 'post'
    ? await axios.post(url, body, config)
    : await axios.get(url, config);

  const contentType = (response.headers['content-type'] || '').toLowerCase();
  if (contentType.includes('application/json')) {
    const text = new TextDecoder().decode(response.data);
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error(text.slice(0, 200) || 'Server returned JSON instead of PDF');
    }
    if (parsed?.message && parsed['0'] === undefined) {
      throw new Error(parsed.message);
    }
    return coercePdfBlob(parsed);
  }

  return coercePdfBlob(response.data, contentType || PDF_MIME);
}

function appendRunDateRangeParams(params, dateRange) {
  if (dateRange?.dateFrom) params.set('dateFrom', dateRange.dateFrom);
  if (dateRange?.dateTo) params.set('dateTo', dateRange.dateTo);
}

function runDateRangeQuery(dateRange) {
  const query = {};
  if (dateRange?.dateFrom) query.dateFrom = dateRange.dateFrom;
  if (dateRange?.dateTo) query.dateTo = dateRange.dateTo;
  return query;
}

export function getSavedReportPreviewUrl(reportId, token, reportDate, dateRange) {
  const authToken = token || localStorage.getItem('token');
  if (!authToken) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token: authToken });
  if (reportDate) {
    params.set('reportDate', reportDate);
  }
  appendRunDateRangeParams(params, dateRange);
  return `/api/report-center/reports/${reportId}/preview?${params.toString()}`;
}

export async function fetchSavedReportHtml(reportId, reportDate, dateRange) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Not signed in');
  const params = { token, ...runDateRangeQuery(dateRange) };
  if (reportDate) params.reportDate = reportDate;
  const response = await axios.get(`/api/report-center/reports/${reportId}/preview`, {
    responseType: 'text',
    skipGlobalErrorToast: true,
    params
  });
  return assertHtmlContent(response.data);
}

export async function fetchSavedReportData(reportId, reportDate, dateRange) {
  const params = { ...runDateRangeQuery(dateRange) };
  if (reportDate) params.reportDate = reportDate;
  const response = await axios.get(`/api/report-center/reports/${reportId}/data`, {
    skipGlobalErrorToast: true,
    params
  });
  return response.data;
}

export async function fetchDraftReportPreviewData({ reportName, pages, aircraftIds, reportDate, dateFrom, dateTo }) {
  const response = await axios.post(
    '/api/report-center/preview-draft/data',
    {
      reportName,
      pages,
      aircraftIds: aircraftIds || [],
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchDraftReportHtml({ reportName, pages, aircraftIds, reportDate, dateFrom, dateTo }) {
  const response = await axios.post(
    '/api/report-center/preview-draft',
    {
      reportName,
      pages,
      aircraftIds: aircraftIds || [],
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

/** Direct browser download via token query — avoids axios/proxy binary corruption */
export function downloadSavedReportPdfViaToken(reportId, filename, reportDate, dateRange) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  if (reportDate) {
    params.set('reportDate', reportDate);
  }
  appendRunDateRangeParams(params, dateRange);
  const url = `/api/report-center/reports/${reportId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function downloadSavedReportPdf(reportId, filename, reportDate, dateRange) {
  downloadSavedReportPdfViaToken(reportId, filename, reportDate, dateRange);
}

export function downloadSavedReportCsvViaToken(reportId, filename, reportDate, dateRange) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  if (reportDate) {
    params.set('reportDate', reportDate);
  }
  appendRunDateRangeParams(params, dateRange);
  const url = `/api/report-center/reports/${reportId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function downloadSavedReportCsv(reportId, filename, reportDate, dateRange) {
  downloadSavedReportCsvViaToken(reportId, filename, reportDate, dateRange);
}

export async function downloadDraftReportCsv({ reportName, pages, aircraftIds, reportDate, dateFrom, dateTo }, filename) {
  const csv = await fetchCsvText('/api/report-center/download-draft-csv', 'post', {
    reportName,
    pages,
    aircraftIds: aircraftIds || [],
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, `${reportName || 'report'}.csv`));
}

export async function downloadSavedReportPdfWithVisualization(
  reportId,
  { filename, reportDate, dateFrom, dateTo, visualizations }
) {
  const blob = await fetchPdfArrayBuffer(`/api/report-center/reports/${reportId}/download-with-visualization`, 'post', {
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined,
    visualizations: visualizations || []
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'report.pdf');
}

export async function downloadSavedVisualizationPdf(reportId, { filename, visualizations }) {
  const blob = await fetchPdfArrayBuffer(`/api/report-center/reports/${reportId}/download-visualization`, 'post', {
    visualizations: visualizations || []
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'visualizations.pdf');
}

export async function downloadDraftReportPdf({ reportName, pages, aircraftIds, reportDate, dateFrom, dateTo, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/download-draft', 'post', {
    reportName,
    pages,
    aircraftIds: aircraftIds || [],
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'report.pdf');
}

export async function fetchDueReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate, filters }) {
  const response = await axios.post(
    '/api/report-center/due-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined,
      filters: filters || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchDueReportHtml({ aircraftIds, dateFrom, dateTo, reportDate, filters }) {
  const response = await axios.post(
    '/api/report-center/due-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined,
      filters: filters || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadDueReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, filters, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/due-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    filters: filters || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Due_Report.pdf');
}

export async function downloadDueReportCsv({ aircraftIds, dateFrom, dateTo, reportDate, filters }, filename) {
  const csv = await fetchCsvText('/api/report-center/due-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    filters: filters || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Due_Report.csv'));
}

export async function fetchDueReportHistory() {
  const response = await axios.get('/api/report-center/due-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadDueReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/due-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Due_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadDueReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/due-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Due_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchFuelConsumptionReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/fuel-consumption-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchFuelConsumptionReportHtml({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/fuel-consumption-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadFuelConsumptionReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/fuel-consumption-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Fuel_Consumption_Report.pdf');
}

export async function downloadFuelConsumptionReportCsv({ aircraftIds, dateFrom, dateTo, reportDate }, filename) {
  const csv = await fetchCsvText('/api/report-center/fuel-consumption-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Fuel_Consumption_Report.csv'));
}

export async function fetchFuelConsumptionReportHistory() {
  const response = await axios.get('/api/report-center/fuel-consumption-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadFuelConsumptionReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/fuel-consumption-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Fuel_Consumption_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadFuelConsumptionReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/fuel-consumption-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Fuel_Consumption_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchOilConsumptionReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/oil-consumption-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchOilConsumptionReportHtml({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/oil-consumption-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadOilConsumptionReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/oil-consumption-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Oil_Consumption_Report.pdf');
}

export async function downloadOilConsumptionReportCsv({ aircraftIds, dateFrom, dateTo, reportDate }, filename) {
  const csv = await fetchCsvText('/api/report-center/oil-consumption-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Oil_Consumption_Report.csv'));
}

export async function fetchOilConsumptionReportHistory() {
  const response = await axios.get('/api/report-center/oil-consumption-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadOilConsumptionReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/oil-consumption-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Oil_Consumption_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadOilConsumptionReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/oil-consumption-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Oil_Consumption_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchFlyingReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/flying-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchFlyingReportHtml({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/flying-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadFlyingReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/flying-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Flying_Report.pdf');
}

export async function downloadFlyingReportCsv({ aircraftIds, dateFrom, dateTo, reportDate }, filename) {
  const csv = await fetchCsvText('/api/report-center/flying-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Flying_Report.csv'));
}

export async function fetchFlyingReportHistory() {
  const response = await axios.get('/api/report-center/flying-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadFlyingReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/flying-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Flying_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadFlyingReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/flying-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Flying_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchEsrReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/esr-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchEsrReportHtml({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/esr-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadEsrReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, visualizations, sectionOverrides }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/esr-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined,
    sectionOverrides: sectionOverrides || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'ESR_Report.pdf');
}

export async function downloadEsrReportCsv({ aircraftIds, dateFrom, dateTo, reportDate, sectionOverrides }, filename) {
  const csv = await fetchCsvText('/api/report-center/esr-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    sectionOverrides: sectionOverrides || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'ESR_Report.csv'));
}

export async function fetchEsrReportHistory() {
  const response = await axios.get('/api/report-center/esr-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadEsrReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/esr-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'ESR_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadEsrReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/esr-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'ESR_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchEnginePowerCheckReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/engine-power-check-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadEnginePowerCheckReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/engine-power-check-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Engine_Power_Check_EPC_Report.pdf');
}

export async function downloadEnginePowerCheckReportCsv({ aircraftIds, dateFrom, dateTo, reportDate }, filename) {
  const csv = await fetchCsvText('/api/report-center/engine-power-check-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Engine_Power_Check_EPC_Report.csv'));
}

export async function fetchEnginePowerCheckReportHistory() {
  const response = await axios.get('/api/report-center/engine-power-check-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadEnginePowerCheckReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/engine-power-check-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Engine_Power_Check_EPC_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadEnginePowerCheckReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/engine-power-check-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Engine_Power_Check_EPC_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchDefectReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/defect-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchDefectReportHtml({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/defect-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadDefectReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/defect-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Defect_Report.pdf');
}

export async function downloadDefectReportCsv({ aircraftIds, dateFrom, dateTo, reportDate }, filename) {
  const csv = await fetchCsvText('/api/report-center/defect-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Defect_Report.csv'));
}

export async function fetchDefectReportHistory() {
  const response = await axios.get('/api/report-center/defect-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadDefectReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/defect-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Defect_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadDefectReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/defect-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Defect_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchMelReleaseReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/mel-release-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchMelReleaseReportHtml({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/mel-release-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadMelReleaseReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/mel-release-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'MEL_Release_Report.pdf');
}

export async function downloadMelReleaseReportCsv({ aircraftIds, dateFrom, dateTo, reportDate }, filename) {
  const csv = await fetchCsvText('/api/report-center/mel-release-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'MEL_Release_Report.csv'));
}

export async function fetchMelReleaseReportHistory() {
  const response = await axios.get('/api/report-center/mel-release-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadMelReleaseReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/mel-release-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'MEL_Release_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadMelReleaseReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/mel-release-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'MEL_Release_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchCrsReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/crs-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchCrsReportHtml({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/crs-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadCrsReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/crs-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'CRS_Report.pdf');
}

export async function downloadCrsReportCsv({ aircraftIds, dateFrom, dateTo, reportDate }, filename) {
  const csv = await fetchCsvText('/api/report-center/crs-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'CRS_Report.csv'));
}

export async function fetchCrsReportHistory() {
  const response = await axios.get('/api/report-center/crs-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadCrsReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/crs-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'CRS_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadCrsReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/crs-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'CRS_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchComponentReplacementReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/component-replacement-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchComponentReplacementReportHtml({ aircraftIds, dateFrom, dateTo, reportDate }) {
  const response = await axios.post(
    '/api/report-center/component-replacement-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadComponentReplacementReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/component-replacement-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Component_Replacement_Report.pdf');
}

export async function downloadComponentReplacementReportCsv({ aircraftIds, dateFrom, dateTo, reportDate }, filename) {
  const csv = await fetchCsvText('/api/report-center/component-replacement-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Component_Replacement_Report.csv'));
}

export async function fetchComponentReplacementReportHistory() {
  const response = await axios.get('/api/report-center/component-replacement-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadComponentReplacementReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/component-replacement-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Component_Replacement_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadComponentReplacementReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/component-replacement-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Component_Replacement_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchRepetitiveDefectReportPreviewData({ aircraftIds, dateFrom, dateTo, minOccurrences, reportDate }) {
  const response = await axios.post(
    '/api/report-center/repetitive-defect-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      minOccurrences,
      reportDate: reportDate || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchRepetitiveDefectReportHtml({ aircraftIds, dateFrom, dateTo, minOccurrences, reportDate }) {
  const response = await axios.post(
    '/api/report-center/repetitive-defect-report/preview',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      minOccurrences,
      reportDate: reportDate || undefined
    },
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadRepetitiveDefectReportPdf({ aircraftIds, dateFrom, dateTo, minOccurrences, reportDate, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/repetitive-defect-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    minOccurrences,
    reportDate: reportDate || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Repetitive_Defect_Report.pdf');
}

export async function downloadRepetitiveDefectReportCsv({ aircraftIds, dateFrom, dateTo, minOccurrences, reportDate }, filename) {
  const csv = await fetchCsvText('/api/report-center/repetitive-defect-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    minOccurrences,
    reportDate: reportDate || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Repetitive_Defect_Report.csv'));
}

export async function fetchRepetitiveDefectReportHistory() {
  const response = await axios.get('/api/report-center/repetitive-defect-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadRepetitiveDefectReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/repetitive-defect-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Repetitive_Defect_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadRepetitiveDefectReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/repetitive-defect-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Repetitive_Defect_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchAdsbReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/adsb-report/preview-data',
    buildAdsbReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchAdsbReportHtml(payload) {
  const response = await axios.post(
    '/api/report-center/adsb-report/preview',
    buildAdsbReportRequestBody(payload),
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadAdsbReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/adsb-report/download', 'post', {
    ...buildAdsbReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'ADSB_Report.pdf');
}

export async function downloadAdsbReportCsv(payload, filename) {
  const csv = await fetchCsvText('/api/report-center/adsb-report/download-csv', 'post', buildAdsbReportRequestBody(payload));
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'ADSB_Report.csv'));
}

function buildAdsbReportRequestBody({
  aircraftIds,
  assemblyIds,
  classifications,
  priorities,
  sources,
  regulatoryAuthorities,
  statusFilter,
  bulletinTypeFilter,
  search,
  dateFrom,
  dateTo,
  reportDate
}) {
  return {
    aircraftIds: aircraftIds || [],
    assemblyIds: assemblyIds || [],
    classifications: classifications || [],
    priorities: priorities || [],
    sources: sources || [],
    regulatoryAuthorities: regulatoryAuthorities || [],
    statusFilter: statusFilter || 'ALL',
    bulletinTypeFilter: bulletinTypeFilter || 'ALL',
    search: search || '',
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined
  };
}

export async function fetchAdsbReportHistory() {
  const response = await axios.get('/api/report-center/adsb-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadAdsbReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/adsb-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'ADSB_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadAdsbReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/adsb-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'ADSB_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchAmpScheduleReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/amp-schedule-report/preview-data',
    buildAmpScheduleReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function fetchAmpScheduleReportHtml(payload) {
  const response = await axios.post(
    '/api/report-center/amp-schedule-report/preview',
    buildAmpScheduleReportRequestBody(payload),
    { responseType: 'text', skipGlobalErrorToast: true }
  );
  return assertHtmlContent(response.data);
}

export async function downloadAmpScheduleReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/amp-schedule-report/download', 'post', {
    ...buildAmpScheduleReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'AMP_Schedule_Report.pdf');
}

export async function downloadAmpScheduleReportCsv(payload, filename) {
  const csv = await fetchCsvText('/api/report-center/amp-schedule-report/download-csv', 'post', buildAmpScheduleReportRequestBody(payload));
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'AMP_Schedule_Report.csv'));
}

function buildAmpScheduleReportRequestBody({
  aircraftIds,
  assemblyIds,
  maintenanceType,
  inspectionCategory,
  scheduleFilter,
  search,
  dateFrom,
  dateTo,
  reportDate
}) {
  return {
    aircraftIds: aircraftIds || [],
    assemblyIds: assemblyIds || [],
    maintenanceType: maintenanceType || 'ALL',
    inspectionCategory: inspectionCategory || '',
    scheduleFilter: scheduleFilter || 'ALL',
    search: search || '',
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined
  };
}

export async function fetchAmpScheduleReportHistory() {
  const response = await axios.get('/api/report-center/amp-schedule-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadAmpScheduleReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/amp-schedule-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'AMP_Schedule_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadAmpScheduleReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/amp-schedule-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'AMP_Schedule_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchAircraftLifecycleReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/aircraft-lifecycle-report/preview-data',
    buildAircraftLifecycleReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadAircraftLifecycleReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/aircraft-lifecycle-report/download', 'post', {
    ...buildAircraftLifecycleReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Aircraft_Lifecycle_Report.pdf');
}

export async function downloadAircraftLifecycleReportCsv(payload, filename) {
  const csv = await fetchCsvText(
    '/api/report-center/aircraft-lifecycle-report/download-csv',
    'post',
    buildAircraftLifecycleReportRequestBody(payload)
  );
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Aircraft_Lifecycle_Report.csv'));
}

function buildAircraftLifecycleReportRequestBody({
  aircraftIds,
  includeArchived,
  search,
  dateFrom,
  dateTo,
  reportDate
}) {
  return {
    aircraftIds: aircraftIds || [],
    includeArchived: Boolean(includeArchived),
    search: search || '',
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined
  };
}

export async function fetchAircraftLifecycleReportHistory() {
  const response = await axios.get('/api/report-center/aircraft-lifecycle-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadAircraftLifecycleReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/aircraft-lifecycle-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Aircraft_Lifecycle_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadAircraftLifecycleReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/aircraft-lifecycle-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Aircraft_Lifecycle_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchComponentReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/component-report/preview-data',
    buildComponentReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadComponentReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/component-report/download', 'post', {
    ...buildComponentReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Component_Report.pdf');
}

export async function downloadComponentReportCsv(payload, filename) {
  const csv = await fetchCsvText(
    '/api/report-center/component-report/download-csv',
    'post',
    buildComponentReportRequestBody(payload)
  );
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Component_Report.csv'));
}

function buildComponentReportRequestBody({
  aircraftIds,
  includeArchived,
  search,
  dateFrom,
  dateTo,
  reportDate
}) {
  return {
    aircraftIds: aircraftIds || [],
    includeArchived: Boolean(includeArchived),
    search: search || '',
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined
  };
}

export async function fetchComponentReportHistory() {
  const response = await axios.get('/api/report-center/component-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadComponentReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/component-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Component_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadComponentReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/component-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Component_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function buildAircraftWiseStockReportRequestBody({
  aircraftIds,
  includeArchived,
  status,
  category,
  search,
  dateFrom,
  dateTo,
  reportDate
}) {
  return {
    aircraftIds: aircraftIds || [],
    includeArchived: Boolean(includeArchived),
    status: status || '',
    category: category || '',
    search: search || '',
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined
  };
}

export async function fetchAircraftWiseStockReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/aircraft-wise-stock-report/preview-data',
    buildAircraftWiseStockReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadAircraftWiseStockReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/aircraft-wise-stock-report/download', 'post', {
    ...buildAircraftWiseStockReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Aircraft_Wise_Stock_Report.pdf');
}

export async function downloadAircraftWiseStockReportCsv(payload, filename) {
  const csv = await fetchCsvText(
    '/api/report-center/aircraft-wise-stock-report/download-csv',
    'post',
    buildAircraftWiseStockReportRequestBody(payload)
  );
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Aircraft_Wise_Stock_Report.csv'));
}

export async function fetchAircraftWiseStockReportHistory() {
  const response = await axios.get('/api/report-center/aircraft-wise-stock-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadAircraftWiseStockReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/aircraft-wise-stock-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Aircraft_Wise_Stock_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadAircraftWiseStockReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/aircraft-wise-stock-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Aircraft_Wise_Stock_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function buildItemIssueHistoryReportRequestBody({
  aircraftIds,
  dateFrom,
  dateTo,
  status,
  partNumber,
  search,
  includeArchived,
  reportDate
}) {
  return {
    aircraftIds: aircraftIds || [],
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    status: status || '',
    partNumber: partNumber || '',
    search: search || '',
    includeArchived: Boolean(includeArchived),
    reportDate: reportDate || undefined
  };
}

export async function fetchItemIssueHistoryReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/item-issue-history-report/preview-data',
    buildItemIssueHistoryReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadItemIssueHistoryReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/item-issue-history-report/download', 'post', {
    ...buildItemIssueHistoryReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Item_Issue_Record_History.pdf');
}

export async function downloadItemIssueHistoryReportCsv(payload, filename) {
  const csv = await fetchCsvText(
    '/api/report-center/item-issue-history-report/download-csv',
    'post',
    buildItemIssueHistoryReportRequestBody(payload)
  );
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Item_Issue_Record_History.csv'));
}

export async function fetchItemIssueHistoryReportHistory() {
  const response = await axios.get('/api/report-center/item-issue-history-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadItemIssueHistoryReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/item-issue-history-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Item_Issue_Record_History.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadItemIssueHistoryReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/item-issue-history-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Item_Issue_Record_History.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function buildLoanInOutReportRequestBody({
  direction,
  dateFrom,
  dateTo,
  status,
  approvalStatus,
  workflowStatus,
  search,
  includeArchived,
  reportDate
}) {
  return {
    direction: direction || 'ALL',
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    status: status || '',
    approvalStatus: approvalStatus || '',
    workflowStatus: workflowStatus || '',
    search: search || '',
    includeArchived: Boolean(includeArchived),
    reportDate: reportDate || undefined
  };
}

export async function fetchLoanInOutReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/loan-in-out-report/preview-data',
    buildLoanInOutReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadLoanInOutReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/loan-in-out-report/download', 'post', {
    ...buildLoanInOutReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Loan_IN_OUT_Record.pdf');
}

export async function downloadLoanInOutReportCsv(payload, filename) {
  const csv = await fetchCsvText(
    '/api/report-center/loan-in-out-report/download-csv',
    'post',
    buildLoanInOutReportRequestBody(payload)
  );
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Loan_IN_OUT_Record.csv'));
}

export async function fetchLoanInOutReportHistory() {
  const response = await axios.get('/api/report-center/loan-in-out-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadLoanInOutReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/loan-in-out-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Loan_IN_OUT_Record.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadLoanInOutReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/loan-in-out-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Loan_IN_OUT_Record.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function buildAgainstPoInOutReportRequestBody({
  direction,
  dateFrom,
  dateTo,
  status,
  workflowStatus,
  search,
  reportDate
}) {
  return {
    direction: direction || 'ALL',
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    status: status || '',
    workflowStatus: workflowStatus || '',
    search: search || '',
    reportDate: reportDate || undefined
  };
}

export async function fetchAgainstPoInOutReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/against-po-in-out-report/preview-data',
    buildAgainstPoInOutReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadAgainstPoInOutReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/against-po-in-out-report/download', 'post', {
    ...buildAgainstPoInOutReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Against_PO_IN_OUT_Record.pdf');
}

export async function downloadAgainstPoInOutReportCsv(payload, filename) {
  const csv = await fetchCsvText(
    '/api/report-center/against-po-in-out-report/download-csv',
    'post',
    buildAgainstPoInOutReportRequestBody(payload)
  );
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Against_PO_IN_OUT_Record.csv'));
}

export async function fetchAgainstPoInOutReportHistory() {
  const response = await axios.get('/api/report-center/against-po-in-out-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadAgainstPoInOutReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/against-po-in-out-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Against_PO_IN_OUT_Record.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadAgainstPoInOutReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/against-po-in-out-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Against_PO_IN_OUT_Record.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function buildShelfLifeExpiryReportRequestBody({
  expiryMode, daysAhead, storeType, status, category, search, includeArchived, dateFrom, dateTo, reportDate
}) {
  return {
    expiryMode: expiryMode || 'ALL_WITH_EXPIRY',
    daysAhead: daysAhead ?? 30,
    storeType: storeType || '',
    status: status || '',
    category: category || '',
    search: search || '',
    includeArchived: Boolean(includeArchived),
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined
  };
}

export async function fetchShelfLifeExpiryReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/shelf-life-expiry-report/preview-data',
    buildShelfLifeExpiryReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadShelfLifeExpiryReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/shelf-life-expiry-report/download', 'post', {
    ...buildShelfLifeExpiryReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Shelf_Life_Expiry_Report.pdf');
}

export async function downloadShelfLifeExpiryReportCsv(payload, filename) {
  const csv = await fetchCsvText(
    '/api/report-center/shelf-life-expiry-report/download-csv',
    'post',
    buildShelfLifeExpiryReportRequestBody(payload)
  );
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Shelf_Life_Expiry_Report.csv'));
}

export async function fetchShelfLifeExpiryReportHistory() {
  const response = await axios.get('/api/report-center/shelf-life-expiry-report/history', { skipGlobalErrorToast: true });
  return response.data.history || [];
}

export function downloadShelfLifeExpiryReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Not signed in');
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/shelf-life-expiry-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Shelf_Life_Expiry_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadShelfLifeExpiryReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Not signed in');
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/shelf-life-expiry-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Shelf_Life_Expiry_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function buildStoreWiseStockReportRequestBody({
  reportKind, storeTypes, status, category, condition, quarantineView, search, includeArchived, dateFrom, dateTo, reportDate
}) {
  return {
    reportKind: reportKind || 'STORE_WISE',
    storeTypes: storeTypes || [],
    status: status || '',
    category: category || '',
    condition: condition || '',
    quarantineView: quarantineView || '',
    search: search || '',
    includeArchived: Boolean(includeArchived),
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate: reportDate || undefined
  };
}

export async function fetchStoreWiseStockReportPreviewData(payload) {
  const response = await axios.post(
    '/api/report-center/store-wise-stock-report/preview-data',
    buildStoreWiseStockReportRequestBody(payload),
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadStoreWiseStockReportPdf(payload, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/store-wise-stock-report/download', 'post', {
    ...buildStoreWiseStockReportRequestBody(payload),
    visualizations: payload.visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Store_Wise_Stock_Report.pdf');
}

export async function downloadStoreWiseStockReportCsv(payload, filename) {
  const csv = await fetchCsvText(
    '/api/report-center/store-wise-stock-report/download-csv',
    'post',
    buildStoreWiseStockReportRequestBody(payload)
  );
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Store_Wise_Stock_Report.csv'));
}

export async function fetchStoreWiseStockReportHistory({ reportKind } = {}) {
  const response = await axios.get('/api/report-center/store-wise-stock-report/history', {
    params: reportKind ? { reportKind } : undefined,
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadStoreWiseStockReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Not signed in');
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/store-wise-stock-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Store_Wise_Stock_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadStoreWiseStockReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Not signed in');
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/store-wise-stock-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Store_Wise_Stock_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchSalesReportPreviewData({ aircraftIds, dateFrom, dateTo, reportDate, filters }) {
  const response = await axios.post(
    '/api/report-center/sales-report/preview-data',
    {
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined,
      filters: filters || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadSalesReportPdf({ aircraftIds, dateFrom, dateTo, reportDate, filters, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/sales-report/download', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    filters: filters || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Flight_Entry_Report.pdf');
}

export async function downloadSalesReportCsv({ aircraftIds, dateFrom, dateTo, reportDate, filters }, filename) {
  const csv = await fetchCsvText('/api/report-center/sales-report/download-csv', 'post', {
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    filters: filters || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Flight_Entry_Report.csv'));
}

export async function fetchSalesReportHistory() {
  const response = await axios.get('/api/report-center/sales-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadSalesReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/sales-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Flight_Entry_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadSalesReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/sales-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Flight_Entry_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function fetchPilotReportPreviewData({ crewId, aircraftIds, dateFrom, dateTo, reportDate, filters }) {
  const response = await axios.post(
    '/api/report-center/pilot-report/preview-data',
    {
      crewId,
      aircraftIds: aircraftIds || [],
      dateFrom,
      dateTo,
      reportDate: reportDate || undefined,
      filters: filters || undefined
    },
    { skipGlobalErrorToast: true }
  );
  return response.data;
}

export async function downloadPilotReportPdf({ crewId, aircraftIds, dateFrom, dateTo, reportDate, filters, visualizations }, filename) {
  const blob = await fetchPdfArrayBuffer('/api/report-center/pilot-report/download', 'post', {
    crewId,
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    filters: filters || undefined,
    visualizations: visualizations || undefined
  });
  await assertPdfBlob(blob);
  triggerBlobDownload(blob, filename || 'Pilot_Report.pdf');
}

export async function downloadPilotReportCsv({ crewId, aircraftIds, dateFrom, dateTo, reportDate, filters }, filename) {
  const csv = await fetchCsvText('/api/report-center/pilot-report/download-csv', 'post', {
    crewId,
    aircraftIds: aircraftIds || [],
    dateFrom,
    dateTo,
    reportDate: reportDate || undefined,
    filters: filters || undefined
  });
  triggerTextDownload(csv, csvFilenameFromPdfName(filename, 'Pilot_Report.csv'));
}

export async function fetchPilotReportHistory() {
  const response = await axios.get('/api/report-center/pilot-report/history', {
    skipGlobalErrorToast: true
  });
  return response.data.history || [];
}

export function downloadPilotReportHistoryPdf(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/pilot-report/history/${historyId}/download?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename || 'Pilot_Report.pdf');
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadPilotReportHistoryCsv(historyId, filename) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not signed in');
  }
  const params = new URLSearchParams({ token });
  const url = `/api/report-center/pilot-report/history/${historyId}/download-csv?${params.toString()}`;
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilenameFromPdfName(filename, 'Pilot_Report.csv'));
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function triggerBlobDownload(blob, filename) {
  const pdfBlob = blob.type ? blob : new Blob([blob], { type: PDF_MIME });
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function openHtmlInNewTab(html) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const popup = window.open(url, '_blank');
  if (!popup) {
    URL.revokeObjectURL(url);
    throw new Error('Pop-up blocked. Use the in-page preview or allow pop-ups.');
  }
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}

export async function resolveAxiosErrorMessage(err) {
  if (err.response?.data) {
    if (err.response.data instanceof ArrayBuffer) {
      const text = new TextDecoder().decode(err.response.data);
      try {
        const parsed = JSON.parse(text);
        return parsed.message || text;
      } catch {
        return text.slice(0, 200);
      }
    }
    return messageFromResponseData(err.response.data);
  }
  return err.message || 'Request failed';
}
